import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
	permissions,
	setCurrentUserRole,
	setSelectedLanguage
} from '../../../../redux/features/auth-slice';
import { roleAccessed } from '../../../../redux/apis/authAPIs';
import { PORTAL_TYPES, PORTAL_TYPES_DEFAULT_ROUTE } from '../../../constants/roles';
import {
	getBranchUserRoles,
	getSystemUserRoles,
	getTenantUserRoles
} from '../../../../redux/selectors/auth/userRoles';
import SwitchAccountItem from './SwitchAccountItem';
import images from '../../../../images';
import { useState } from 'react';
import Alert from '../../errors/components/Alert';
import { toAbsoluteUrl } from '../../../../_metronic/helpers';

const SwitchAccount = () => {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [loading, setLoading] = useState<boolean>(false);
	const [loaderIndex, setLoaderIndex] = useState<string>('');
	const [list, setList] = useState<any>([]);

	const systemRoles = useSelector(getSystemUserRoles);
	const tenantRoles = useSelector(getTenantUserRoles);
	const branchRoles = useSelector(getBranchUserRoles);
	const onClickAccess = async (user: any, indexString: any, linkTenant?: any) => {
		setLoading(() => true);
		setLoaderIndex(() => indexString);
		const data: any = {};
		data.userRoleId = user._id;
		if (user?.role_id?.portal_type === PORTAL_TYPES.TENANT_PORTAL) {
			if (linkTenant) {
				data.tenantId = linkTenant._id;
			} else {
				data.tenantId = user.tenant_id._id;
			}
		}
		if (user?.role_id?.portal_type === PORTAL_TYPES.SYSTEM_PORTAL) {
			const langObject = {
				lang: 'en',
				name: 'English',
				flag: toAbsoluteUrl('/media/flags/united-states.svg')
			};
			await dispatch(setSelectedLanguage(langObject));
			await i18n.changeLanguage('en');
		}

		//user roles permissions to modules of tenants
		const permission = tenantRoles.find((item: any) => item?._id === user?._id)
			?.role_id?.permission;
		const roleName = tenantRoles.find((item: any) => item?._id === user?._id)?.role_id
			?.name;

		dispatch(permissions(permission));
		if (user?.role_id?.portal_type === PORTAL_TYPES.BRANCH_PORTAL) {
			const permission = branchRoles.find((item: any) => item?._id === user?._id)
				?.role_id?.permission;
			dispatch(permissions(permission));
		}

		const response = await dispatch(roleAccessed(data));
		if (response.type == 'roleAccessed/rejected') {
			setList([{ message: t(response?.payload?.message) }]);
			setLoading(() => false);
			return;
		}
		if (linkTenant) {
			const createdUser = {
				...user,
				tenant_id: {
					...user.tenant_id,
					country: linkTenant.country,
					name: linkTenant.name,
					preferred_language: linkTenant.preferred_language,
					subscription_end_date: linkTenant.subscription_end_date,
					subscription_start_date: linkTenant.subscription_start_date,
					_id: linkTenant._id
				}
			};
			dispatch(setCurrentUserRole(createdUser));
		} else {
			dispatch(setCurrentUserRole(user));
		}

		const portalType: string = user.role_id.portal_type;
		let defaultRoute = PORTAL_TYPES_DEFAULT_ROUTE[portalType];

		if (roleName === 'Contributor') {
			defaultRoute = PORTAL_TYPES_DEFAULT_ROUTE['CONTRIBUTOR'];
		} else if (roleName === 'Warehouse Clerk') {
			defaultRoute = PORTAL_TYPES_DEFAULT_ROUTE['WAREHOUSE_CLERK'];
		} else if (roleName === 'Accountant') {
			defaultRoute = PORTAL_TYPES_DEFAULT_ROUTE['ACCOUNTANT'];
		}
		setLoading(() => false);
		navigate(defaultRoute);
	};

	const TitleItem = ({ title }: any) => (
		<div className='text-gray-500 fw-bold fs-4 d-flex w-100 fs-12px'>
			<span className='fs-12px text-uppercase' style={{ marginTop: '16px' }}>
				{t(title)}
			</span>
		</div>
	);

	return (
		<>
			<div className='d-flex flex-column justify-content-center align-items-center h-100 switch-account-wrapper'>
				<Alert Tostlist={list} setList={setList} />
				<div className='d-flex flex-center align-items-center flex-column bg-body shadow-sm margin padding-40 mx-auto switch-account-card radius-12px'>
					<img
						alt='Logo'
						src={images.logos.logo1}
						className='marginBottom-40px h-24px'
					/>
					{systemRoles.map((user: any, index: number) => (
						<SwitchAccountItem
							key={`SYSTEM_PORTAL${index}`}
							name={t('system_portal')}
							onAccess={() => onClickAccess(user, 'S' + index)}
							loading={loaderIndex == 'S' + index && loading}
							disabled={loading}
						/>
					))}
					<div className='switch-page'>
						{tenantRoles.length > 0 && <TitleItem title='accounts' />}
						{tenantRoles.map((user: any, index: number) => {
							return (
								<>
									<SwitchAccountItem
										key={`TENANT_PORTAL${index}`}
										name={user?.tenant_id?.name}
										onAccess={() => onClickAccess(user, 'T' + index)}
										disabled={user?.is_active === false || loading}
										loading={loaderIndex == 'T' + index && loading}
									/>
									{user?.tenant_id?.linked_tenants?.map(
										(linkTenant: any, indx: any) => (
											<SwitchAccountItem
												key={`TENANT_PORTAL${index}${indx}`}
												name={linkTenant?.name}
												onAccess={() =>
													onClickAccess(
														user,
														'T' + index + indx,
														linkTenant
													)
												}
												// disabled={user?.is_active === false || loading}
												loading={
													loaderIndex == 'T' + index + indx &&
													loading
												}
											/>
										)
									)}
								</>
							);
						})}
						{branchRoles.length > 0 && <TitleItem title='branches' />}
						{branchRoles.map((user: any, index: number) => (
							<SwitchAccountItem
								key={`BRANCH_PORTAL${index}`}
								name={user?.branch_id?.name}
								disabled={user?.is_active === false || loading}
								onAccess={() => onClickAccess(user, 'B' + index)}
								loading={loaderIndex == 'B' + index && loading}
							/>
						))}
						{Array(0)
							.fill('')
							.map((index: number) => {
								return (
									<SwitchAccountItem
										key={`RANDOM${index}`}
										name={`Random ${index}`}
										onAccess={() => {}}
										loading={loading}
									/>
								);
							})}
					</div>
				</div>
			</div>
		</>
	);
};

export default SwitchAccount;
